import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  LBNodeData,
  TextTemplateChunk,
} from '@/proto/logiblocks/logiblocks';
import TeamUserSelect, {
  type TeamData,
  type TeamUserSelection,
} from '@/components/TeamUserSelect';
import { GetUsersByTeamIds } from '@/apis/settings';
import useToken from '@/common/helper/useToken';
import { useTeamGroups } from '@/hooks/useTeamGroups';
import styles from '../NodeCustomization.module.css';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import { FieldErrorLabel, FieldLabel } from '../shared';
import {
  teamUserSelectionToTeamNotifRecipients,
  teamNotifRecipientsToTeamUserSelection,
  apiTeamsResponseToTeamData,
  apiTeamsResponseToAdditionalOptions,
  withLogiblocksEmailRoles,
  type AdditionalOption,
} from './recipientUtils';
import { EmailActionFields, EmailActionSendMode } from './EmailActionNodeInfo';
import {
  CustomButton,
  CustomRadioGroup,
  CustomShipmentText,
} from '@/components';
import { HeaderAddIcon } from '@/assets/icons/icons';
import { MinusOutlined } from '@ant-design/icons';
import { Flex } from 'antd';

/**
 * Customization UI for EMAIL_ACTION nodes.
 */
export const EmailActionCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const recipientsError = validationErrors?.find(
    (e) => e.field === EmailActionFields.RECIPIENTS
  );
  const subjectError = validationErrors?.find(
    (e) => e.field === EmailActionFields.SUBJECT
  );
  const bodyError = validationErrors?.find(
    (e) => e.field === EmailActionFields.BODY
  );
  const { getToken } = useToken();
  const [teamsData, setTeamsData] = useState<TeamData[]>([]);
  const [additionalOptions, setAdditionalOptions] = useState<
    AdditionalOption[]
  >([]);
  const [teamsLoading, setTeamsLoading] = useState(true);

  const emailData = nodeData.email_action;
  // Team groups expand to their team teams in the recipient pickers.
  const teamGroups = useTeamGroups(teamsData.map((team) => team.teamId));

  const toSelection = useMemo(
    () => teamNotifRecipientsToTeamUserSelection(emailData?.to ?? []),
    [emailData?.to]
  );

  const recipientCount = useMemo(() => {
    const uids = new Set<string>();
    for (const r of emailData?.to ?? []) {
      if (r.select_all) {
        const team = teamsData.find((t) => t.teamId === r.team_id);
        if (team) {
          for (const user of team.users) {
            uids.add(user.id);
          }
        } else {
          uids.add(`team-placeholder-${r.team_id}`);
        }
      } else {
        for (const uid of r.user_ids) {
          uids.add(uid);
        }
      }
    }
    return uids.size;
  }, [emailData?.to, teamsData]);

  const ccSelection = useMemo(
    () => teamNotifRecipientsToTeamUserSelection(emailData?.cc ?? []),
    [emailData?.cc]
  );
  const bccSelection = useMemo(
    () => teamNotifRecipientsToTeamUserSelection(emailData?.bcc ?? []),
    [emailData?.bcc]
  );

  const hasCcBccData =
    (emailData?.cc?.length ?? 0) > 0 || (emailData?.bcc?.length ?? 0) > 0;
  const [showCcBcc, setShowCcBcc] = useState(hasCcBccData);

  // Always show cc/bcc if we have data in the protobuf
  useEffect(() => {
    if (hasCcBccData) {
      setShowCcBcc(true);
    }
  }, [hasCcBccData]);

  // Fetch teams with users on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      setTeamsLoading(false);
      return;
    }
    setTeamsLoading(true);
    GetUsersByTeamIds(token, [])
      .then((res) => {
        const data = res?.data?.data;
        if (data?.is_success) {
          setTeamsData(
            apiTeamsResponseToTeamData(data?.data?.teams_users ?? [])
          );
          setAdditionalOptions(
            withLogiblocksEmailRoles(
              apiTeamsResponseToAdditionalOptions(data?.data?.additional ?? [])
            )
          );
        }
      })
      .catch((err) => {
        console.error('Failed to fetch teams for email action', err);
      })
      .finally(() => {
        setTeamsLoading(false);
      });
  }, [getToken]);

  /** Merge a partial update into the current emailAction data. */
  const buildUpdatedData = useCallback(
    (
      patch: Partial<NonNullable<LBNodeData['email_action']>>
    ): Partial<LBNodeData> => ({
      email_action: {
        to: emailData?.to ?? [],
        cc: emailData?.cc ?? [],
        bcc: emailData?.bcc ?? [],
        send_mode: emailData?.send_mode || EmailActionSendMode.SEPARATE,
        subject_chunks: emailData?.subject_chunks ?? [],
        body_chunks: emailData?.body_chunks ?? [],
        ...patch,
      },
    }),
    [emailData]
  );

  // If recipient count drops to 1 or less (and loading has finished), reset sendMode to default ('separate')
  useEffect(() => {
    if (!teamsLoading && recipientCount <= 1) {
      if (
        emailData?.send_mode &&
        emailData.send_mode !== EmailActionSendMode.SEPARATE
      ) {
        onChange(
          buildUpdatedData({
            send_mode: EmailActionSendMode.SEPARATE,
          })
        );
      }
    }
  }, [
    teamsLoading,
    recipientCount,
    emailData?.send_mode,
    onChange,
    buildUpdatedData,
  ]);

  const handleToChange = useCallback(
    (selection: TeamUserSelection) => {
      onChange(
        buildUpdatedData({
          to: teamUserSelectionToTeamNotifRecipients(selection, teamsData),
        })
      );
    },
    [onChange, buildUpdatedData, teamsData]
  );

  const handleCcChange = useCallback(
    (selection: TeamUserSelection) => {
      onChange(
        buildUpdatedData({
          cc: teamUserSelectionToTeamNotifRecipients(selection, teamsData),
        })
      );
    },
    [onChange, buildUpdatedData, teamsData]
  );

  const handleBccChange = useCallback(
    (selection: TeamUserSelection) => {
      onChange(
        buildUpdatedData({
          bcc: teamUserSelectionToTeamNotifRecipients(selection, teamsData),
        })
      );
    },
    [onChange, buildUpdatedData, teamsData]
  );

  const handleSubjectChange = useCallback(
    (subjectChunks: TextTemplateChunk[]) => {
      onChange(buildUpdatedData({ subject_chunks: subjectChunks }));
    },
    [onChange, buildUpdatedData]
  );

  const handleBodyChange = useCallback(
    (bodyChunks: TextTemplateChunk[]) => {
      onChange(buildUpdatedData({ body_chunks: bodyChunks }));
    },
    [onChange, buildUpdatedData]
  );
  const handleToggleCcBcc = useCallback(() => {
    setShowCcBcc((prev) => {
      const next = !prev;
      if (!next) {
        onChange(
          buildUpdatedData({
            cc: [],
            bcc: [],
          })
        );
      }
      return next;
    });
  }, [onChange, buildUpdatedData]);

  return (
    <>
      <div className={styles.customizationSection}>
        <Flex
          justify='space-between'
          align='center'
          style={{ marginBottom: 8 }}
        >
          <FieldLabel
            required
            className={styles.sectionLabel}
            style={{ marginBottom: 0 }}
          >
            Recipients
          </FieldLabel>
        </Flex>
        {teamsLoading ? (
          <div>Loading teams...</div>
        ) : (
          <TeamUserSelect
            teamsData={teamsData}
            additionalOptions={additionalOptions}
            teamGroups={teamGroups}
            value={toSelection}
            onChange={handleToChange}
            disabled={isReadOnly}
            placeholder='Select recipients...'
          />
        )}
        <FieldErrorLabel error={recipientsError} />
      </div>
      {recipientCount > 1 && (
        <div className={styles.customizationSection}>
          <CustomRadioGroup
            value={emailData?.send_mode || EmailActionSendMode.SEPARATE}
            onChange={(e) => {
              onChange(
                buildUpdatedData({
                  send_mode: e.target.value,
                })
              );
            }}
            disabled={isReadOnly}
            options={[
              {
                label: 'Separate emails',
                value: EmailActionSendMode.SEPARATE,
              },
              {
                label: 'One shared email',
                value: EmailActionSendMode.SHARED,
              },
            ]}
          />
        </div>
      )}
      <div className={styles.customizationSection}>
        <Flex align='center' gap={16}>
          <CustomButton
            onClick={handleToggleCcBcc}
            text={showCcBcc ? 'Remove Cc/Bcc' : 'Add Cc/Bcc'}
            bgColor='var(--light-gray)'
            color='var(--text-primary)'
            height='28px'
            width='auto'
            fontSize='12px'
            fontWeight='600'
            boxShadow='none'
            borderRadius='8px'
            icon={showCcBcc ? <MinusOutlined /> : <HeaderAddIcon />}
            iconPosition='left'
            gap={'8px'}
            isFormValid={isReadOnly}
            padding={'0px 8px'}
          />
          {showCcBcc &&
            emailData?.send_mode === EmailActionSendMode.SEPARATE && (
              <CustomShipmentText
                text={'Cc’s and Bcc’s will be added to each email.'}
                fontSize='10px'
              />
            )}
        </Flex>
      </div>

      {showCcBcc && (
        <>
          <div className={styles.customizationSection}>
            <label className={styles.sectionLabel}>CC</label>
            {teamsLoading ? (
              <div>Loading teams...</div>
            ) : (
              <TeamUserSelect
                teamsData={teamsData}
                additionalOptions={additionalOptions}
                teamGroups={teamGroups}
                value={ccSelection}
                onChange={handleCcChange}
                disabled={isReadOnly}
                placeholder='Select CC recipients...'
              />
            )}
          </div>
          <div className={styles.customizationSection}>
            <label className={styles.sectionLabel}>BCC</label>
            {teamsLoading ? (
              <div>Loading teams...</div>
            ) : (
              <TeamUserSelect
                teamsData={teamsData}
                additionalOptions={additionalOptions}
                teamGroups={teamGroups}
                value={bccSelection}
                onChange={handleBccChange}
                disabled={isReadOnly}
                placeholder='Select BCC recipients...'
              />
            )}
          </div>
        </>
      )}

      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Subject
        </FieldLabel>
        <TemplatedTextArea
          nodeId={nodeId}
          chunks={emailData?.subject_chunks ?? []}
          onChange={handleSubjectChange}
          placeholder='Email subject...'
          multiline={false}
          disabled={isReadOnly}
        />
        <FieldErrorLabel error={subjectError} />
      </div>

      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Body
        </FieldLabel>
        <TemplatedTextArea
          nodeId={nodeId}
          chunks={emailData?.body_chunks ?? []}
          onChange={handleBodyChange}
          placeholder='Email body...'
          disabled={isReadOnly}
        />
        <FieldErrorLabel error={bodyError} />
      </div>
    </>
  );
};
